import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { I18nKey } from "../../i18n/i18n";
import { Button } from "./Button";
import { FILTER_CONTROL, FILTER_LABEL, FilterSelect } from "./FilterControls";

type FilterOption = {
  value: string | number;
  label: I18nKey;
};

type TableFiltersProps = {
  filterValue: string;
  onFilterChange: (value: string) => void;
  filterOptions: FilterOption[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onDownload?: () => void;
  resultCount?: number;
  showResultCount?: boolean;
  onHelpClick?: () => void;
  customFilter?: ReactNode;
};

export const TableFilters = ({
  filterValue,
  onFilterChange,
  filterOptions,
  searchValue,
  onSearchChange,
  onDownload,
  resultCount,
  showResultCount,
  onHelpClick,
  customFilter,
}: TableFiltersProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col md:flex-row items-start md:items-end gap-3 my-6">
      {customFilter ? (
        customFilter
      ) : (
        <label className={`hidden min-w-[200px] ${FILTER_LABEL}`}>
          {t("filter-by")}
          <FilterSelect
            value={filterValue}
            onChange={(e) => onFilterChange(e.target.value)}
          >
            {filterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.label)}
              </option>
            ))}
          </FilterSelect>
        </label>
      )}

      <div className="w-full flex-1 flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <label className="block text-sm">{t("filter-search")}</label>
          <div className="flex items-center gap-2">
            {showResultCount && typeof resultCount === "number" && (
              <span className="text-xs text-grey-medium whitespace-nowrap">
                {resultCount} {resultCount > 1 ? t("results") : t("result")}
              </span>
            )}
            {onHelpClick && (
              <button
                type="button"
                onClick={onHelpClick}
                className="w-5 h-5 flex items-center justify-center rounded-full border border-grey-medium text-grey-medium hover:bg-grey-light"
                title={t("filter-search-help")}
              >
                <FontAwesomeIcon icon="info-circle" className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        <div className="relative">
          <FontAwesomeIcon
            icon="magnifying-glass"
            className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-grey-medium pointer-events-none"
          />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filter-search")}
            className={`${FILTER_CONTROL} w-full pl-8 pr-8`}
          />
          {searchValue && (
            <button
              onClick={() => onSearchChange("")}
              aria-label={t("filter-clear")}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <FontAwesomeIcon
                icon="times"
                className="h-3 w-3 text-grey-medium"
              />
            </button>
          )}
        </div>
      </div>

      {onDownload && (
        <div className="hidden md:flex items-center gap-2">
          <Button
            onClick={onDownload}
            variant="secondary"
            size="small"
            className="h-8 w-8 !p-0 flex items-center justify-center"
          >
            <FontAwesomeIcon icon="download" />
          </Button>
        </div>
      )}
    </div>
  );
};
